/* One slide in the slideshow timeline: thumb, ordering controls, and the
   per-slide motion/duration config. */
import {
  ArrowDownIcon,
  ArrowUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import type { SlideMotion } from "../KenBurnsPlayer"
import { MOTION_ICON, MOTION_LABELS } from "./constants"
import type { Slide } from "./types"

interface SlideCardProps {
  index: number
  slide: Slide
  total: number
  disabled?: boolean
  onRemove: () => void
  onMove: (direction: -1 | 1) => void
  onUpdate: (patch: Partial<Slide>) => void
}

export default function SlideCard({
  index,
  slide,
  total,
  disabled,
  onRemove,
  onMove,
  onUpdate,
}: SlideCardProps) {
  const isVideo = slide.asset.type === "video"
  const thumb = slide.asset.thumbnailUrl || slide.asset.url
  return (
    <div className="flex-shrink-0 w-24 rounded-lg overflow-hidden bg-system-gray-900 ring-1 ring-system-gray-700/50">
      {/* Thumb + hover controls + corner badges. */}
      <div className="relative aspect-square group">
        {isVideo && !slide.asset.thumbnailUrl ? (
          <video
            src={slide.asset.url}
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          thumb && (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          )
        )}
        <span className="absolute top-0.5 left-0.5 bg-brand text-white text-[9px] font-bold px-1 py-0.5 rounded">
          {index + 1}
        </span>
        <span
          className="absolute top-0.5 right-0.5 bg-black/70 text-white text-[10px] font-bold px-1 rounded"
          title={isVideo ? "Video slide" : MOTION_LABELS[slide.motion]}
        >
          {isVideo ? "▶" : MOTION_ICON[slide.motion]}
        </span>
        <span className="absolute bottom-0.5 left-0.5 bg-black/70 text-white text-[9px] font-bold px-1 py-0.5 rounded">
          {(slide.durationMs / 1000).toFixed(1)}s
        </span>
        <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-0.5">
          <button
            onClick={() => onMove(-1)}
            disabled={disabled || index === 0}
            className="w-5 h-5 rounded bg-system-gray-700 text-white hover:bg-system-gray-600 disabled:opacity-30 flex items-center justify-center"
            title="Move earlier"
          >
            <ArrowUpIcon className="w-2.5 h-2.5 rotate-[-90deg]" />
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={disabled || index === total - 1}
            className="w-5 h-5 rounded bg-system-gray-700 text-white hover:bg-system-gray-600 disabled:opacity-30 flex items-center justify-center"
            title="Move later"
          >
            <ArrowDownIcon className="w-2.5 h-2.5 rotate-[-90deg]" />
          </button>
          <button
            onClick={onRemove}
            disabled={disabled}
            className="w-5 h-5 rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-30 flex items-center justify-center"
            title="Remove"
          >
            <XMarkIcon className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
      {/* Compact controls below the thumb — single-line selects/sliders.
          Ken Burns motion only applies to image slides; video slides play
          their own footage so the motion select is disabled for them. */}
      <div className="p-1 space-y-1">
        <select
          value={slide.motion}
          onChange={e => onUpdate({ motion: e.target.value as SlideMotion })}
          disabled={disabled || isVideo}
          className="select select-xs w-full bg-system-gray-800 border-system-gray-700 text-white text-[10px] focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
          title={isVideo ? "Video slides play as-is" : "Motion direction"}
        >
          {(Object.keys(MOTION_LABELS) as SlideMotion[]).map(m => (
            <option key={m} value={m}>
              {MOTION_ICON[m]} {MOTION_LABELS[m]}
            </option>
          ))}
        </select>
        <input
          type="range"
          min={500}
          max={6000}
          step={250}
          value={slide.durationMs}
          onChange={e => onUpdate({ durationMs: Number(e.target.value) })}
          disabled={disabled}
          className="range range-xs range-primary w-full"
          title={`${(slide.durationMs / 1000).toFixed(1)}s`}
        />
      </div>
    </div>
  )
}
